import { Link } from "react-router-dom";

import {
  buttonVariants,
  DataCell,
  DataRow,
  DataTable,
  LoadingOverlay,
} from "@breakingthelines/design-system";

import { useArchetypes } from "@/hooks/use-archetypes";

import { formatArchetypeDate, positionsLine, statusLabel } from "./archetype-form";
import styles from "./archetypes.module.css";

const TABLE_COLUMNS =
  "minmax(170px, 1.2fr) minmax(220px, 1.6fr) minmax(90px, 0.6fr) minmax(80px, 0.5fr) minmax(70px, 0.5fr) minmax(110px, 0.7fr)";

function errorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : "Failed to load archetypes.";
}

/**
 * The archetype library (profile-spec.md §5): one row per archetype, in
 * library order. Status is the version readers see; a draft waiting to be
 * published shows in its own column.
 */
export function ArchetypesPage() {
  const { archetypes, loading, failed, error } = useArchetypes();

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Archetypes</h1>
        <Link className={buttonVariants({})} to="/archetypes/new">
          New archetype
        </Link>
      </div>

      <section className={styles.tableSection}>
        {loading ? <LoadingOverlay label="Loading archetypes..." /> : null}
        <DataTable
          label="Archetypes"
          columns={TABLE_COLUMNS}
          header={["Name", "Home positions", "Status", "Published", "Draft", "Updated"]}
          headerClassName={styles.tableHeader}
        >
          {archetypes.map((archetype) => (
            <DataRow key={archetype.archetypeId} className={styles.tableRow}>
              <DataCell>
                <Link className={styles.name} to={`/archetypes/${archetype.archetypeId}`}>
                  {archetype.name || archetype.archetypeId}
                </Link>
                {archetype.requiredData === "event" ? (
                  <span className={styles.muted}> Event data only</span>
                ) : null}
              </DataCell>
              <DataCell label="Home positions" className={styles.meta}>
                {positionsLine(archetype.homePositions)}
              </DataCell>
              <DataCell label="Status" className={styles.meta}>
                {statusLabel(archetype.status)}
              </DataCell>
              <DataCell label="Published" className={styles.figure}>
                {archetype.publishedVersion > 0 ? `v${archetype.publishedVersion}` : ""}
              </DataCell>
              <DataCell label="Draft" className={styles.figure}>
                {archetype.draftVersion > 0 ? `v${archetype.draftVersion}` : ""}
              </DataCell>
              <DataCell label="Updated" className={styles.meta}>
                {formatArchetypeDate(archetype.updatedAt)}
              </DataCell>
            </DataRow>
          ))}
        </DataTable>

        {!loading && failed ? <div className={styles.emptyState}>{errorMessage(error)}</div> : null}
        {!loading && !failed && archetypes.length === 0 ? (
          <div className={styles.emptyState}>No archetypes yet.</div>
        ) : null}
      </section>
    </div>
  );
}
